/**
 * Cinematic Shot Description Builder
 *
 * Given the structured shot returned by the pre-production endpoint (which
 * contains `preProduction.character_actions` timelines, dialogues, cinematography,
 * etc.), constructs a plain-text `description` string in the exact "cinematic
 * opener + Panel N (mm:ss.s - mm:ss.s):" format the storyboard UI expects.
 *
 * Feb 2026 enhancements:
 *   • PER-PANEL characters/elements — each panel lists only the entities that
 *     actually appear in that time window (a character who speaks in panel 2
 *     is NOT dumped into panel 1's Main Elements).
 *   • PER-PANEL camera movement — each panel gets its own cinematic move
 *     picked from a rotation informed by the panel's beat type (dialogue →
 *     close-up / medium two-shot, action → tracking / dolly, etc.).
 *   • Screenplay-style DIALOGUE parsing — Voice Over per panel comes from
 *     dialogue lines that fall inside that panel's time window.
 *
 * Pure JS. No React, no network.
 */

import { parsePanelsFromDescription } from './panelParser';
import { parseDialoguesFromDescription, resolveDialogueCharacterIds } from './dialogueParser';

// ────────── Small formatting helpers ──────────
const SECOND_IN_MS = 1000;

/**
 * Format a floating-point second value as "mm:ss.s".
 * Examples:  0 → "00:00.0"   6.5 → "00:06.5"   65 → "01:05.0"
 */
const formatMmSs = (seconds) => {
    const s = Math.max(0, Number(seconds) || 0);
    const mm = Math.floor(s / 60);
    const ss = s - mm * 60;
    const ssStr = ss.toFixed(1).padStart(4, '0');
    return `${String(mm).padStart(2, '0')}:${ssStr}`;
};

const parseTimecodeToSeconds = (value) => {
    if (value == null) return null;
    const str = String(value).trim();
    if (!str) return null;
    if (str.includes(':')) {
        const parts = str.split(':');
        const mm = parseInt(parts[0], 10) || 0;
        const ss = parseFloat(parts[1]) || 0;
        return mm * 60 + ss;
    }
    const n = parseFloat(str);
    return Number.isFinite(n) ? n : null;
};

const cleanText = (v) => {
    if (v == null) return '';
    return String(v).replace(/\s+/g, ' ').trim();
};

const escapeRegExp = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const wholeWordRegex = (name) => new RegExp(`\\b${escapeRegExp(name)}\\b`, 'i');

// ────────── Beat flattening ──────────

const flattenCharacterActionTimeline = (shot) => {
    const preProd = shot?.preProduction || {};
    const raw = preProd.character_actions || preProd.actions || [];
    if (!Array.isArray(raw) || raw.length === 0) return [];

    const out = [];
    for (const charAction of raw) {
        const charName = charAction.character_name || charAction.characterName || '';
        const timeline = Array.isArray(charAction.timeline) ? charAction.timeline : [];
        for (const entry of timeline) {
            const startSec = parseTimecodeToSeconds(entry.timecode || entry.time || 0) ?? 0;
            const durMs = Number(entry.duration_ms) || 2000;
            const endSec = startSec + durMs / SECOND_IN_MS;
            const action = cleanText(entry.action || entry.description || '');
            if (!action) continue;
            out.push({
                startSec,
                endSec,
                charName,
                action,
                emotion: cleanText(entry.emotion || ''),
            });
        }
    }
    out.sort((a, b) => a.startSec - b.startSec);
    return out;
};

// ────────── Panel windows ──────────

const divideIntoPanelWindows = (numPanels, totalSeconds, beats, dialogues) => {
    const N = Math.max(1, Math.min(8, Math.round(numPanels)));
    const T = Math.max(1, Number(totalSeconds) || 15);

    // Feb 2026 — when dialogue count matches panel count, give each panel
    // exactly ONE dialogue. Windows are drawn at the midpoints between
    // consecutive dialogue start times so every dialogue's __inferredStart
    // falls cleanly inside its own panel window. This matches the intuitive
    // author expectation "5 dialogues → 5 panels, one per line".
    const dialogueStarts = (dialogues || [])
        .map(d => d.__inferredStart)
        .filter(s => Number.isFinite(s) && s >= 0 && s < T);
    if (dialogueStarts.length === N && N > 1) {
        const bounds = [0];
        for (let i = 1; i < N; i++) {
            const mid = (dialogueStarts[i - 1] + dialogueStarts[i]) / 2;
            const rounded = Math.round(mid * 10) / 10;
            const prev = bounds[bounds.length - 1];
            bounds.push(Math.max(prev + 0.5, Math.min(T - 0.5, rounded)));
        }
        bounds.push(T);
        const windows = [];
        for (let i = 0; i < N; i++) {
            windows.push({ startSec: bounds[i], endSec: bounds[i + 1] });
        }
        return windows;
    }

    // Otherwise: fall back to anchor-based division from beats + dialogue starts.
    const beatAnchors = beats
        .map(b => b.startSec)
        .filter(s => Number.isFinite(s) && s >= 0 && s < T);
    const anchors = Array.from(new Set([...beatAnchors, ...dialogueStarts])).sort((a, b) => a - b);

    let bounds = [];
    if (anchors.length >= N) {
        const picked = [];
        for (let i = 0; i < N; i++) {
            const idx = Math.round((anchors.length - 1) * (i / N));
            picked.push(anchors[idx]);
        }
        picked[0] = 0;
        for (let i = 1; i < picked.length; i++) {
            if (picked[i] <= picked[i - 1]) picked[i] = Math.min(T - 0.5, picked[i - 1] + 0.5);
        }
        bounds = [...picked, T];
    } else {
        for (let i = 0; i <= N; i++) {
            bounds.push((T * i) / N);
        }
    }

    bounds = bounds.map((b, i) => (i === 0 ? 0 : i === bounds.length - 1 ? T : Math.round(b * 10) / 10));

    const windows = [];
    for (let i = 0; i < N; i++) {
        windows.push({ startSec: bounds[i], endSec: bounds[i + 1] });
    }
    return windows;
};

// ────────── Dialogue timing inference ──────────

/**
 * Estimate the speaking duration of a dialogue in seconds — same formula the
 * pre-production modal uses (words / 2.5, or chars × 0.08, whichever is larger,
 * with a floor of 2).
 */
const estimateSpeechDuration = (text) => {
    if (!text || !text.trim()) return 0;
    const wordCount = text.trim().split(/\s+/).length;
    const charCount = text.trim().length;
    const wordEstimate = Math.ceil(wordCount / 2.5);
    const charEstimate = Math.ceil(charCount * 0.08);
    return Math.max(2, Math.max(wordEstimate, charEstimate) + 1);
};

/**
 * Given parsed dialogues (in reading order) and total shot duration, stamp
 * each dialogue with an inferred `__inferredStart` and `__inferredEnd` by
 * laying them out sequentially with small gaps, scaled to fit the shot.
 */
const inferDialogueTiming = (dialogues, totalSeconds) => {
    if (!Array.isArray(dialogues) || dialogues.length === 0) return [];
    const T = Math.max(1, Number(totalSeconds) || 15);

    // Raw durations
    const raws = dialogues.map(d => estimateSpeechDuration(d.text));
    const rawTotal = raws.reduce((a, b) => a + b, 0) || 1;

    // If the raw total exceeds T (or is much less), scale to fit ~80% of T
    // so there's breathing room at head/tail.
    const target = Math.min(T * 0.9, Math.max(T * 0.5, rawTotal));
    const scale = target / rawTotal;
    const gapTotal = Math.max(0, T - target);
    const gapEach = gapTotal / (dialogues.length + 1);

    let cursor = gapEach;
    return dialogues.map((d, i) => {
        const dur = Math.max(1.5, raws[i] * scale);
        const start = Math.min(T - 0.5, cursor);
        const end = Math.min(T, start + dur);
        cursor = end + gapEach;
        return { ...d, __inferredStart: start, __inferredEnd: end };
    });
};

// ────────── Per-panel entity resolution ──────────

/**
 * For a panel window, resolve the characters + elements that ACTUALLY appear
 * IN FRAME during that panel.
 *
 * Priority (mutually exclusive — first match wins):
 *   A) If the panel has dialogue speakers → Main Elements = ONLY those
 *      speakers (single-speaker close-ups are close-ups). Elements are
 *      pulled from the dialogue text only.
 *   B) If the panel has character-action beats → Main Elements = char names
 *      + elements mentioned in those beats.
 *   C) If the panel has NEITHER (silent/reaction panel):
 *      • For the ESTABLISHING (first) panel, scan the opening action
 *        paragraph so the wide shot reveals who's setting the scene.
 *      • For any other empty panel, the caller carries forward the
 *        previous panel's cast (continuity).
 *
 * We deliberately DO NOT dump every mention from the raw description into
 * every panel — a two-shot of Tara & Kabir doesn't mean panel 3's close-up
 * on Tara should also show Kabir.
 */
const resolvePanelEntities = ({
    beatsInWindow,
    dialoguesInWindow,
    characters,
    elements,
    isFirstPanel,
    firstActionLine,
}) => {
    const chars = Array.isArray(characters) ? characters : [];
    const elems = Array.isArray(elements) ? elements : [];

    const charNames = new Set();
    const elemNames = new Set();

    // Element scan (used by both A and B branches — elements mentioned by
    // name in dialogue/beat text are considered in-frame).
    const scanForElements = (text) => {
        if (!text) return;
        for (const e of elems) {
            const name = (e?.name || '').trim();
            if (!name) continue;
            if (wholeWordRegex(name).test(text)) elemNames.add(name);
        }
    };

    // ── Priority A: dialogue speakers own the panel ──
    if (dialoguesInWindow.length > 0) {
        for (const d of dialoguesInWindow) {
            const speakerName = d.characterName || d.rawCue;
            if (speakerName) charNames.add(speakerName);
        }
        // Elements: from the DIALOGUE TEXT only (not the broader action).
        // A close-up on Tara saying "caramel latte" only puts the mug in
        // frame if "mug" is explicitly in her line.
        const dialogueText = dialoguesInWindow.map(d => d.text).join(' ');
        scanForElements(dialogueText);
    }
    // ── Priority B: character-action beats (no dialogue in window) ──
    else if (beatsInWindow.length > 0) {
        for (const b of beatsInWindow) {
            if (b.charName) charNames.add(b.charName);
        }
        // Also scan the beat action text for OTHER project characters mentioned
        // by name (e.g. "Kabir walks toward Tara" — Tara should be listed too).
        const beatText = beatsInWindow.map(b => `${b.charName} ${b.action}`).join(' ');
        for (const c of chars) {
            const name = (c?.name || '').trim();
            if (!name) continue;
            if (wholeWordRegex(name).test(beatText)) charNames.add(name);
        }
        scanForElements(beatText);
    }
    // ── Priority C: silent panel ──
    else if (isFirstPanel && firstActionLine) {
        // Establishing panel: reveal who's in the opening scene.
        for (const c of chars) {
            const name = (c?.name || '').trim();
            if (!name) continue;
            if (wholeWordRegex(name).test(firstActionLine)) charNames.add(name);
        }
        for (const e of elems) {
            const name = (e?.name || '').trim();
            if (!name) continue;
            if (wholeWordRegex(name).test(firstActionLine)) elemNames.add(name);
        }
    }
    // For non-first empty panels, we return {} and the CALLER carries
    // forward the previous panel's cast.

    // Filter char names down to project characters when possible (dedupes casing)
    const resolvedChars = Array.from(charNames).map(name => {
        const match = chars.find(c => (c?.name || '').toLowerCase() === name.toLowerCase());
        return match?.name || name;
    });
    // Dedupe case-insensitively while preserving insertion order.
    const seen = new Set();
    const finalChars = [];
    for (const n of resolvedChars) {
        const key = n.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        finalChars.push(n);
    }

    const resolvedElems = Array.from(elemNames);

    // Establishing-panel fallback: if the first panel still came up empty,
    // seed with the primary character so the wide shot isn't left blank.
    if (finalChars.length === 0 && resolvedElems.length === 0 && isFirstPanel && chars.length > 0) {
        finalChars.push(chars[0].name);
    }

    return { charNames: finalChars, elemNames: resolvedElems };
};

// ────────── Per-panel camera movement ──────────

const CAMERA_MOVES = {
    dialogue_single: [
        'CLOSE-UP push-in on the speaker',
        'MEDIUM CLOSE-UP static on the speaker',
        'MEDIUM SHOT slow drift on the speaker',
    ],
    dialogue_two_shot: [
        'MEDIUM TWO-SHOT static across the counter',
        'OVER-THE-SHOULDER angle from the listener',
        'MEDIUM TWO-SHOT slow pan between speakers',
    ],
    dialogue_reaction: [
        'CLOSE-UP static on the listener\'s reaction',
        'CLOSE-UP slow push-in on the reaction',
    ],
    action: [
        'TRACKING SHOT following the action',
        'HANDHELD PAN across the movement',
        'DOLLY forward through the space',
        'CRANE DOWN into the scene',
    ],
    establishing: [
        'WIDE ESTABLISHING SHOT slow push-in',
        'WIDE ANGLE static reveal',
        'AERIAL / CRANE DOWN establishing the space',
    ],
    closing: [
        'SLOW PULL-BACK from the subject',
        'CLOSE-UP hold on the final beat',
        'MEDIUM SHOT slow rack-focus to end',
    ],
    default: [
        'MEDIUM SHOT steady framing',
        'DOLLY-IN on the subject',
        'PAN LEFT across the scene',
    ],
};

/**
 * Pick a camera move for a panel based on its beat type + position.
 */
const pickCameraMove = ({ panelIndex, numPanels, dialoguesInWindow, beatsInWindow, hasDialogueBefore, hasDialogueAfter }) => {
    // Establishing / closing panels get their own pool.
    if (panelIndex === 0) {
        return CAMERA_MOVES.establishing[panelIndex % CAMERA_MOVES.establishing.length];
    }
    if (panelIndex === numPanels - 1) {
        return CAMERA_MOVES.closing[panelIndex % CAMERA_MOVES.closing.length];
    }
    // Dialogue-carrying panel
    if (dialoguesInWindow.length > 0) {
        const uniqueSpeakers = new Set(dialoguesInWindow.map(d => d.characterName || d.rawCue)).size;
        if (uniqueSpeakers >= 2) {
            return CAMERA_MOVES.dialogue_two_shot[panelIndex % CAMERA_MOVES.dialogue_two_shot.length];
        }
        return CAMERA_MOVES.dialogue_single[panelIndex % CAMERA_MOVES.dialogue_single.length];
    }
    // Reaction beat (right after dialogue, no new dialogue)
    if (hasDialogueBefore && !hasDialogueAfter) {
        return CAMERA_MOVES.dialogue_reaction[panelIndex % CAMERA_MOVES.dialogue_reaction.length];
    }
    // Action beat
    if (beatsInWindow.length > 0) {
        return CAMERA_MOVES.action[panelIndex % CAMERA_MOVES.action.length];
    }
    return CAMERA_MOVES.default[panelIndex % CAMERA_MOVES.default.length];
};

// ────────── Opener ──────────

const buildOpenerLead = (shot) => {
    const cine = shot?.cinematography || shot?.preProduction?.cinematography || {};
    const shotSize = cleanText(cine.shotSize || cine.shot_size || 'Medium');
    const movement = cleanText(cine.movement || cine.camera_movement || '');
    const device   = cleanText(cine.device || cine.camera_device || '');

    const parts = [];
    if (shotSize) parts.push(shotSize);
    if (movement) parts.push(movement);
    parts.push('SHOT');
    const lead = parts.join(' ').toUpperCase();
    const suffix = device ? ` via ${device}` : '';
    return `${lead}${suffix}`;
};

const buildOpenerParagraph = (shot, rawDescription, totalSeconds, characters, elements, dialogues) => {
    const lead = buildOpenerLead(shot);

    const charNames = (characters || []).map(c => c?.name).filter(Boolean).slice(0, 4);
    const elemNames = (elements || []).map(e => e?.name).filter(Boolean).slice(0, 4);
    const subjectBits = [];
    if (charNames.length) subjectBits.push(charNames.join(' and '));
    if (elemNames.length) subjectBits.push(`with ${elemNames.join(', ')}`);

    // Prefer LLM narrative when present, else strip screenplay CUEs out of
    // the raw draft so the opener doesn't read like a script transcript.
    const narrative = cleanText(
        shot?.preProduction?.visual_description
        || shot?.preProduction?.narrative
        || shot?.preProduction?.summary
        || stripScreenplayCues(rawDescription, dialogues)
    );

    const opener = [
        `${lead}`,
        subjectBits.length ? `that follows ${subjectBits.join(' ')}` : '',
        narrative ? `— ${narrative}` : '',
        `Duration: ${Math.round(totalSeconds)} seconds.`,
    ].filter(Boolean).join(' ');

    return opener;
};

/**
 * Remove CUE + dialogue lines from the raw description so the opener uses
 * only the action / stage direction text. Handles both bare-CUE (name on its
 * own line, dialogue below) and inline (`NAME (paren): text`) formats.
 */
const stripScreenplayCues = (rawDescription, dialogues) => {
    let s = String(rawDescription || '');
    // Pass 1 — strip entire INLINE CUE+dialogue lines. Do this line-by-line
    // so we can drop the whole `NAME (voice over): Hey, how are you` line
    // regardless of the specific text content.
    s = s.split(/\r?\n/).filter(line => {
        const t = line.trim();
        if (!t) return true;
        // Line looks like `NAME[(paren)]?\s*:\s*something` where NAME is all caps.
        return !/^[A-Z][A-Z0-9 .'&\-\/]{0,58}?\s*(?:\([^)]*\))?\s*:\s*\S/.test(t);
    }).join('\n');
    // Pass 2 — for each parsed dialogue, also remove the CUE line and the
    // dialogue-text run in case Pass 1 left anything behind (e.g. bare CUE
    // on its own line followed by dialogue on next lines).
    for (const d of dialogues || []) {
        const cue = (d.rawCue || d.characterName || d.character || '').trim();
        if (cue) {
            const cueRe = new RegExp(`^\\s*${escapeRegExp(cue)}(?:\\s*\\([^)]*\\))?\\s*:?\\s*$`, 'gim');
            s = s.replace(cueRe, '');
        }
        const text = (d.text || '').trim();
        if (text) {
            const textRe = new RegExp(escapeRegExp(text).replace(/\s+/g, '\\s+'), 'i');
            s = s.replace(textRe, '');
        }
    }
    // Collapse whitespace runs.
    return s.replace(/\s+/g, ' ').trim();
};

// ────────── Panel block ──────────

const buildPanelBlock = (index, window, opts) => {
    const {
        panelCharNames,
        panelElemNames,
        dialoguesInWindow,
        beatsInWindow,
        storyPurpose,
        visualComposition,
        cameraMovement,
        textOnScreen,
    } = opts;
    const dur = Math.max(0.1, Math.round((window.endSec - window.startSec) * 10) / 10);

    // Main elements: characters first (as they act), then props/elements.
    const mainList = [...panelCharNames, ...panelElemNames];
    const mainElements = cleanText(mainList.join(', ')) || 'Primary subject';

    // Voice Over: concatenate the dialogues that fall inside this window.
    let voiceOver = 'none';
    if (dialoguesInWindow.length > 0) {
        voiceOver = dialoguesInWindow
            .map(d => `${d.characterName || d.rawCue || 'CHARACTER'}: ${d.text}`)
            .join(' | ');
    }

    const startStr = formatMmSs(window.startSec);
    const endStr   = formatMmSs(window.endSec);

    return [
        `Panel ${index} (${startStr} - ${endStr}):`,
        `  - Duration: ${dur}s`,
        `  - Story Purpose: ${cleanText(storyPurpose) || 'Advance the shot beat.'}`,
        `  - Visual Composition: ${cleanText(visualComposition) || 'Continuous framing on the primary subject.'}`,
        `  - Main Elements: ${mainElements}`,
        `  - Camera Movement: ${cleanText(cameraMovement) || 'Steady framing'}`,
        `  - Text On Screen: ${cleanText(textOnScreen) || 'none'}`,
        `  - Voice Over: ${voiceOver}`,
    ].join('\n');
};

// ────────── Panel content synthesis ──────────

/**
 * Compose a panel's Story Purpose + Visual Composition from its beats +
 * dialogues + panel position.
 */
const buildPanelNarrative = ({ panelIndex, numPanels, beatsInWindow, dialoguesInWindow, panelCharNames }) => {
    // Story Purpose priority: dialogue → beat action → position-based generic.
    let storyPurpose = '';
    if (dialoguesInWindow.length > 0) {
        const speakers = Array.from(new Set(dialoguesInWindow.map(d => d.characterName || d.rawCue))).filter(Boolean);
        if (speakers.length === 1) {
            storyPurpose = `Deliver ${speakers[0]}'s line and land its emotional beat.`;
        } else if (speakers.length >= 2) {
            storyPurpose = `Play the exchange between ${speakers.slice(0, 2).join(' and ')}.`;
        }
    } else if (beatsInWindow.length > 0) {
        storyPurpose = cleanText(beatsInWindow[0].action);
    } else if (panelIndex === 0) {
        storyPurpose = 'Establish the location and introduce the subject.';
    } else if (panelIndex === numPanels - 1) {
        storyPurpose = 'Land the closing beat of the shot.';
    } else {
        storyPurpose = 'Advance the shot beat.';
    }

    // Visual Composition
    let visualComposition = '';
    if (dialoguesInWindow.length > 0) {
        const speakers = Array.from(new Set(dialoguesInWindow.map(d => d.characterName || d.rawCue))).filter(Boolean);
        const firstText = cleanText(dialoguesInWindow[0].text).slice(0, 90);
        if (speakers.length >= 2) {
            visualComposition = `Two-shot framing between ${speakers.slice(0, 2).join(' and ')}, focus on the exchange: "${firstText}${firstText.length >= 90 ? '…' : ''}"`;
        } else {
            visualComposition = `Focus tight on ${speakers[0] || 'the speaker'} as they say "${firstText}${firstText.length >= 90 ? '…' : ''}"`;
        }
    } else if (beatsInWindow.length > 0) {
        visualComposition = beatsInWindow
            .map(b => b.charName ? `${b.charName} ${b.action.replace(/^([A-Z])/, m => m.toLowerCase())}` : b.action)
            .filter(Boolean)
            .join('; ');
    } else if (panelIndex === 0 && panelCharNames.length > 0) {
        visualComposition = `Wide of the location — ${panelCharNames.join(' and ')} in frame.`;
    } else {
        visualComposition = 'Continuous framing on the primary subject within the shot.';
    }

    return { storyPurpose, visualComposition };
};

// ────────── Main entry ──────────

/**
 * Build a full cinematic shot description string.
 *
 * @param {object} params
 * @param {object} params.shot            — enriched shot returned by preproduction
 * @param {string} params.rawDescription  — user's original plain-text draft
 * @param {number} params.numPanels       — desired panel count (1-8)
 * @param {number} params.totalSeconds    — total shot duration
 * @param {Array}  params.characters      — resolved character list attached to this shot
 * @param {Array}  params.elements        — resolved element list attached to this shot
 * @param {Array}  [params.projectCharacters] — full project cast (used for
 *                                              resolving CUE names → characterIds)
 * @returns {{ description: string, dialogues: Array }}
 *          `dialogues` is the flat array in the shape shot.dialogues[] expects.
 */
export const buildCinematicShotDescription = ({
    shot = {},
    rawDescription = '',
    numPanels = 4,
    totalSeconds = 15,
    characters = [],
    elements = [],
    projectCharacters = null,
} = {}) => {
    const N = Math.max(1, Math.min(8, Math.round(numPanels)));
    const T = Math.max(1, Number(totalSeconds) || 15);

    // 1) Parse screenplay dialogues out of the raw description
    const rawDialogues = parseDialoguesFromDescription(rawDescription);
    const resolvedDialogues = resolveDialogueCharacterIds(
        rawDialogues,
        Array.isArray(projectCharacters) ? projectCharacters : (characters || [])
    );

    // 2) Infer per-dialogue timing so we can bucket by panel window
    const timedDialogues = inferDialogueTiming(resolvedDialogues, T);

    // 3) Character-action beats from preproduction
    const beats = flattenCharacterActionTimeline(shot);

    // 4) Panel windows (anchored on beats + dialogue starts)
    const windows = divideIntoPanelWindows(N, T, beats, timedDialogues);

    // 5) Opener
    const opener = buildOpenerParagraph(shot, rawDescription, T, characters, elements, timedDialogues);

    // Extract the first action line (prose before the first CUE) from the
    // raw draft — used to seed the establishing panel's characters.
    const firstActionLine = (() => {
        const lines = String(rawDescription || '').split(/\r?\n/);
        const parts = [];
        for (const l of lines) {
            const t = l.trim();
            if (!t) continue;
            // Stop at the first CUE line (all-caps token)
            if (/^[A-Z][A-Z0-9 .'&\-\/]*(?:\s*\([^)]*\))?$/.test(t) && (t.match(/[A-Z]/g) || []).length >= 2) {
                // But allow scene headings like "INT. CAFÉ - NIGHT" through as context.
                if (!/^(INT\.|EXT\.|INT\.\/EXT\.|FADE|CUT|DISSOLVE)/i.test(t)) break;
            }
            parts.push(t);
        }
        return parts.join(' ');
    })();

    // 6) Build each panel block
    const blocks = [];
    let lastResolvedChars = [];
    let lastResolvedElems = [];
    for (let i = 0; i < N; i++) {
        const w = windows[i];
        const beatsInWindow = beats.filter(b => b.startSec >= w.startSec && b.startSec < w.endSec);
        const dialoguesInWindow = timedDialogues.filter(d =>
            d.__inferredStart >= w.startSec && d.__inferredStart < w.endSec
        );

        let { charNames: panelCharNames, elemNames: panelElemNames } = resolvePanelEntities({
            beatsInWindow,
            dialoguesInWindow,
            characters,
            elements,
            isFirstPanel: i === 0,
            firstActionLine,
        });

        // Mid-panel fallback: if this panel has no entities of its own,
        // carry forward the previous panel's cast instead of dumping the
        // full project. Keeps continuity across reaction / silent panels.
        if (panelCharNames.length === 0 && panelElemNames.length === 0) {
            panelCharNames = [...lastResolvedChars];
            panelElemNames = [...lastResolvedElems];
        }
        if (panelCharNames.length > 0 || panelElemNames.length > 0) {
            lastResolvedChars = panelCharNames;
            lastResolvedElems = panelElemNames;
        }

        const { storyPurpose, visualComposition } = buildPanelNarrative({
            panelIndex: i,
            numPanels: N,
            beatsInWindow,
            dialoguesInWindow,
            panelCharNames,
        });

        const hasDialogueBefore = i > 0 && timedDialogues.some(d => d.__inferredStart < w.startSec);
        const hasDialogueAfter  = i < N - 1 && timedDialogues.some(d => d.__inferredStart >= w.endSec);
        const cameraMovement = pickCameraMove({
            panelIndex: i,
            numPanels: N,
            dialoguesInWindow,
            beatsInWindow,
            hasDialogueBefore,
            hasDialogueAfter,
        });

        blocks.push(buildPanelBlock(i + 1, w, {
            panelCharNames,
            panelElemNames,
            dialoguesInWindow,
            beatsInWindow,
            storyPurpose,
            visualComposition,
            cameraMovement,
            textOnScreen: 'none',
        }));
    }

    // 7) Emit the shot.dialogues[] payload the pre-production modal reads
    const shotDialogues = timedDialogues.map((d, i) => ({
        characterId: d.characterId || null,
        characterName: d.characterName || d.rawCue || '',
        text: d.text,
        order: i,
        index: i,
        startTime: formatMmSs(d.__inferredStart),
        endTime: formatMmSs(d.__inferredEnd),
        isVO: !!d.isVO,
    }));

    return {
        description: [opener, ...blocks].join('\n\n'),
        dialogues: shotDialogues,
    };
};

/**
 * If the backend already emitted a well-formed description with N panels
 * that parse cleanly, use it verbatim. Otherwise rebuild client-side.
 * Always ALSO returns the parsed dialogues so the caller can stamp them
 * onto shot.dialogues even when the backend description is trusted.
 */
export const ensureCinematicShotDescription = ({
    shot = {},
    rawDescription = '',
    numPanels = 4,
    totalSeconds = 15,
    characters = [],
    elements = [],
    projectCharacters = null,
} = {}) => {
    const N = Math.max(1, Math.min(8, Math.round(numPanels)));
    const backendDesc = String(shot?.description || '').trim();

    // Always parse dialogues from the raw draft so we can surface them to
    // the pre-production modal even when the backend description is kept.
    const rawDialogues = parseDialoguesFromDescription(rawDescription);
    const resolvedDialogues = resolveDialogueCharacterIds(
        rawDialogues,
        Array.isArray(projectCharacters) ? projectCharacters : (characters || [])
    );
    const timedDialogues = inferDialogueTiming(resolvedDialogues, totalSeconds);
    const dialoguesForShot = timedDialogues.map((d, i) => ({
        characterId: d.characterId || null,
        characterName: d.characterName || d.rawCue || '',
        text: d.text,
        order: i,
        index: i,
        startTime: formatMmSs(d.__inferredStart),
        endTime: formatMmSs(d.__inferredEnd),
        isVO: !!d.isVO,
    }));

    const parsed = parsePanelsFromDescription(backendDesc);
    const looksCinematic = /Panel\s+\d+\s*\(\s*\d{1,2}:\d{2}/i.test(backendDesc);
    if (parsed.length >= N && looksCinematic) {
        return { description: backendDesc, dialogues: dialoguesForShot };
    }

    return buildCinematicShotDescription({
        shot, rawDescription, numPanels: N, totalSeconds, characters, elements, projectCharacters,
    });
};

export default buildCinematicShotDescription;
